import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const navigate = useNavigate();
  const hasPolled = useRef(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/cart');
      return;
    }

    if (hasPolled.current) return;
    hasPolled.current = true;

    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`,
          { withCredentials: true }
        );

        if (data.payment_status === 'paid') {
          setStatus('success');
          toast.success('Payment successful!');
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (error) {
        setStatus('error');
        toast.error('Failed to verify payment');
      }
    };

    pollStatus();
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen">
      <Header />

      <div className="container mx-auto px-4 py-20">
        <div className="max-w-md mx-auto text-center">
          {status === 'checking' && (
            <div data-testid="payment-checking-status">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold font-heading mb-2">Processing Payment...</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="payment-success-status">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-4">
                <CheckCircle className="h-12 w-12 text-accent" />
              </div>
              <h2 className="text-2xl font-bold font-heading mb-2">Order Confirmed!</h2>
              <p className="text-muted-foreground mb-8">
                Thank you for your purchase. Your order has been placed successfully.
              </p>
              <div className="space-y-3">
                <Button className="w-full rounded-full" onClick={() => navigate('/orders')} data-testid="view-orders-button">
                  View My Orders
                </Button>
                <Button variant="outline" className="w-full rounded-full" onClick={() => navigate('/products')} data-testid="continue-shopping-success-button">
                  Continue Shopping
                </Button>
              </div>
            </div>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <div data-testid="payment-error-status">
              <h2 className="text-2xl font-bold font-heading mb-2 text-destructive">Payment Issue</h2>
              <p className="text-muted-foreground mb-8">
                {status === 'expired'
                  ? 'Your payment session has expired.'
                  : status === 'timeout'
                  ? 'Payment verification timed out. Please check your orders.'
                  : 'There was an issue processing your payment.'}
              </p>
              <Button onClick={() => navigate('/cart')} data-testid="back-to-cart-button">Back to Cart</Button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
